import { useCallback, useMemo, useRef } from "react";
import type { Moment } from "moment";
import { makeStyles } from "tss-react/mui";
import { MiButton, MiChip, MiIcon, MiIconButton } from "@miplatform/components";
import { Colors } from "@miplatform/types";
import {
  mdiChevronDown,
  mdiChevronUp,
  mdiSortAlphabeticalVariant,
  mdiSwapVertical,
} from "@mdi/js";
import { Box, SxProps, Typography } from "@mui/material";
import {
  AssignmentUserSortBy,
  AssignmentsTab,
  DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT,
} from "./AssignmentsCalendar.constants";
import type { IAssignmentCalendarUser } from "./AssignmentsCalendar.types";
import { AssignmentsUsersRenderer } from "./AssignmentsUsersRenderer";
import { HorizontalScrollContainer } from "./HorizontalScrollContainer";

interface AssignmentsCrewContainerProps {
  title: string;
  date: Moment;
  users: IAssignmentCalendarUser[];
  selectedUser: IAssignmentCalendarUser | null;
  isCollapsed: boolean;
  isCollapseDisabled?: boolean;
  activeTab?: string;
  activeSubTab?: string;
  minHeight?: number;
  userSortBy: AssignmentUserSortBy;
  sx?: SxProps;
  onSelectedUserChange?: (user: IAssignmentCalendarUser | null) => void;
  onUserSortByChange: (sortBy: AssignmentUserSortBy) => void;
  onUnassignAssignment: (assignmentId: string) => void;
  onCollapseClick: () => void;
}

export const AssignmentsCrewContainer = ({
  title,
  date,
  users,
  selectedUser,
  isCollapsed,
  isCollapseDisabled = false,
  activeTab,
  activeSubTab,
  minHeight,
  userSortBy,
  sx,
  onSelectedUserChange,
  onUserSortByChange,
  onUnassignAssignment,
  onCollapseClick,
}: AssignmentsCrewContainerProps) => {
  const lastSortedUserIdsRef = useRef<string[]>([]);
  const { classes } = useStyles({ isCollapsed, minHeight });
  const sortedUsers = useMemo(() => {
    const sortByOrdersCount = (
      a: IAssignmentCalendarUser,
      b: IAssignmentCalendarUser,
      isDesc?: boolean
    ) => {
      const value = isDesc
        ? b.currentDateAssignments.length - a.currentDateAssignments.length
        : a.currentDateAssignments.length - b.currentDateAssignments.length;

      return value === 0
        ? (a.displayName || "").localeCompare(b.displayName || "")
        : value;
    };

    const userMap = new Map(users.map((u) => [u.id, u]));

    if (selectedUser?.id && lastSortedUserIdsRef.current.length > 0) {
      const preservedList: IAssignmentCalendarUser[] = [];

      lastSortedUserIdsRef.current.forEach((id) => {
        const updatedUser = userMap.get(id);
        if (updatedUser) {
          preservedList.push(updatedUser);
          userMap.delete(id);
        }
      });

      userMap.forEach((newUser) => preservedList.push(newUser));

      return preservedList;
    }

    const sorted = (() => {
      switch (userSortBy) {
        case AssignmentUserSortBy.OrdersCount:
          return [...users].sort((a, b) => sortByOrdersCount(a, b));
        case AssignmentUserSortBy.OrdersCountDesc:
          return [...users].sort((a, b) => sortByOrdersCount(a, b, true));
        default:
          return users;
      }
    })();

    lastSortedUserIdsRef.current = sorted.map((u) => u.id);

    return sorted;
  }, [users, userSortBy, selectedUser?.id]);
  const { userGrid, noWorkCount } = useMemo(() => {
    if (activeTab === AssignmentsTab.Unassigned) {
      return {
        userGrid: [[], []],
        noWorkCount: users.filter(
          (user) => user.currentDateAssignments.length === 0
        ).length,
      };
    }

    const firstHalf: IAssignmentCalendarUser[] = [];
    const secondHalf: IAssignmentCalendarUser[] = [];
    let noWorkCount = 0;

    sortedUsers.forEach((user, index) => {
      if (index % 2 === 0) {
        firstHalf.push(user);
      } else {
        secondHalf.push(user);
      }
      if (user.currentDateAssignments.length === 0) {
        noWorkCount++;
      }
    });

    return { userGrid: [firstHalf, secondHalf], noWorkCount };
  }, [activeTab, sortedUsers]);
  const usersResetKey = `${activeTab}-${activeSubTab || ""}-${users.length}`;

  return (
    <Box className={classes.root} sx={sx}>
      <AssignmentsCrewContainerHeader
        title={title}
        total={users.length}
        isCollapsed={isCollapsed}
        isCollapseDisabled={isCollapseDisabled}
        noWorkCount={noWorkCount}
        sortBy={userSortBy}
        onCollapseClick={onCollapseClick}
        onSortByChange={onUserSortByChange}
      />
      {!isCollapsed && (
        <HorizontalScrollContainer
          disableScroll
          reset={usersResetKey}
          scrollX={DEFAULT_ASSIGNMENTS_CARD_SCROLL_AMOUNT}
          sx={{ height: "100%", flex: 1, gap: 1 }}
          scrollContainerSx={{
            gap: 1,
            ...(activeTab !== AssignmentsTab.Unassigned && {
              flexDirection: "column",
            }),
          }}
        >
          {activeTab === AssignmentsTab.Unassigned ? (
            <AssignmentsUsersRenderer
              date={date}
              users={sortedUsers}
              selectedUser={selectedUser}
              minHeight={minHeight}
              sortBy={userSortBy}
              onSelectedUserChange={onSelectedUserChange}
              onUnassignAssignment={onUnassignAssignment}
            />
          ) : (
            Boolean(users.length) &&
            userGrid.map((columnGroup, index) => (
              <Box display="flex" gap={1} flex={1} key={index}>
                <AssignmentsUsersRenderer
                  isCurrentDateOnly
                  date={date}
                  users={columnGroup}
                  selectedUser={selectedUser}
                  minHeight={minHeight}
                  onUnassignAssignment={onUnassignAssignment}
                />
              </Box>
            ))
          )}
          {users.length === 0 && (
            <Box className={classes.emptyContainer}>
              <Typography className={classes.emptyText}>
                No Users Available
              </Typography>
            </Box>
          )}
        </HorizontalScrollContainer>
      )}
    </Box>
  );
};

interface AssignmentsCrewContainerHeaderProps {
  title: string;
  total?: number;
  isCollapsed: boolean;
  isCollapseDisabled?: boolean;
  noWorkCount?: number;
  sortBy: AssignmentUserSortBy;
  onSortByChange: (sortBy: AssignmentUserSortBy) => void;
  onCollapseClick: () => void;
}

const AssignmentsCrewContainerHeader = ({
  title,
  total,
  noWorkCount,
  isCollapsed,
  isCollapseDisabled,
  sortBy,
  onSortByChange,
  onCollapseClick,
}: AssignmentsCrewContainerHeaderProps) => {
  const { theme, classes } = useStyles({ isCollapsed });

  const handleNoWorkClick = useCallback(() => {
    onSortByChange(AssignmentUserSortBy.OrdersCount);
  }, [onSortByChange]);

  const handleSortAlphabeticalClick = useCallback(() => {
    onSortByChange(AssignmentUserSortBy.Alphabetical);
  }, [onSortByChange]);

  const handleSwapVerticalClick = useCallback(() => {
    onSortByChange(AssignmentUserSortBy.OrdersCountDesc);
  }, [onSortByChange]);

  return (
    <Box className={classes.headerRoot}>
      {total !== 0 && total && (
        <MiChip
          label={total}
          size="small"
          color="error"
          sx={{ height: 14, fontSize: 11 }}
        />
      )}
      <Typography className={classes.title}>{title}</Typography>
      <Box
        display="flex"
        gap={1}
        alignItems="center"
        sx={{ overflowX: "auto", overflowY: "hidden" }}
      >
        <Typography fontSize={theme.fontSize.small} sx={{ minWidth: 43 }}>
          Sort By:
        </Typography>
        <MiButton
          title={`No Work (${noWorkCount})`}
          size="small"
          color={Colors.Grey}
          sx={{
            height: 18,
            fontSize: theme.fontSize.small,
            fontWeight: theme.fontWeight.regular,
            backgroundColor: theme.palette.grey.mediumLight,
            padding: theme.spacing(0, 2),
            ...(sortBy === AssignmentUserSortBy.OrdersCount && {
              backgroundColor: theme.palette.grey.darkAccent,
            }),
          }}
          onClick={handleNoWorkClick}
        />
        <MiIconButton
          sx={{
            padding: 0,
            ...(sortBy === AssignmentUserSortBy.Alphabetical && {
              backgroundColor: theme.palette.grey.darkAccent,
            }),
            borderRadius: theme.spacing(1),
          }}
          onClick={handleSortAlphabeticalClick}
        >
          <MiIcon
            path={mdiSortAlphabeticalVariant}
            color={theme.palette.common.white}
            size={0.75}
          />
        </MiIconButton>
        <MiIconButton
          sx={{
            padding: 0,
            ...(sortBy === AssignmentUserSortBy.OrdersCountDesc && {
              backgroundColor: theme.palette.grey.darkAccent,
            }),
            borderRadius: theme.spacing(1),
          }}
          onClick={handleSwapVerticalClick}
        >
          <MiIcon
            path={mdiSwapVertical}
            color={theme.palette.common.white}
            size={0.75}
          />
        </MiIconButton>
        <MiIconButton
          disabled={isCollapseDisabled}
          sx={{ padding: 0 }}
          onClick={onCollapseClick}
        >
          <MiIcon
            path={isCollapsed ? mdiChevronUp : mdiChevronDown}
            color={theme.palette.common.white}
            size={0.75}
          />
        </MiIconButton>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{ isCollapsed?: boolean; minHeight?: number }>()(
  (theme, { isCollapsed = false, minHeight } = {}) => ({
    root: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      height: isCollapsed ? "auto" : "100%",
      flex: 1,
    },
    headerRoot: {
      display: "flex",
      alignItems: "center",
      color: theme.palette.common.white,
      padding: theme.spacing(2, 3),
      gap: theme.spacing(2.5),
      height: 28,
      backgroundColor: theme.palette.grey.darkerAccent,
      borderTopLeftRadius: theme.spacing(1),
      borderTopRightRadius: theme.spacing(1),
      overflowY: "hidden",
    },
    title: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.semiBold,
      flex: 1,
      minWidth: 61,
    },
    emptyContainer: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
      width: "100%",
      minHeight: minHeight || 260,
      backgroundColor: theme.palette.primary.light,
    },
    emptyText: {
      fontSize: theme.fontSize.medium,
      fontWeight: theme.fontWeight.semiBold,
    },
  })
);
